import type {
  NodeRendererContext,
  LinkRendererContext,
  States,
  Bounds,
  DiagramLabelPosition,
  DiagramNodeData,
  DiagramLinkData,
  DiagramLayoutOutput,
  Dimensions,
  PromotedLinkId
} from './diagram.types';
import { DiagramNodeLayer } from './DiagramNodeLayer';
import { ComponentChildren, RefObject } from 'preact';
import type { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { getBounds, getLinksForNode } from './utils/diagramUtils';

/**
 * Props for Diagram layers component
 */
type DiagramLayersProps<K1, K2, D1, D2, L> = {
  nodes: D1[];
  links: D2[];
  nodeRenderer: (context: NodeRendererContext<D1>) => ComponentChildren;
  linkRenderer?: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  states: States<K1, K2, D2>;
  nodeBounds: Map<K1, Bounds>;
  // width height are for svg for links
  width: number;
  height: number;
  onNodeSizeChanged: (width: number, height: number, id: K1) => void;
  onLinkSizeChanged: (width: number, height: number, id: K2) => void;
  onPromotedLinkSizeChanged: (width: number, height: number, id: string) => void;
  nodeLabelPositions: Map<K1, DiagramLabelPosition>;
  linkLabelPositions: Map<K2, DiagramLabelPosition>;
  labelDimensions: Map<K1 | K2, TextDimensions>;
  supportsSelection: boolean;
  layoutOutput?: L;
  combinedNodeBounds: Bounds;
  zoom: number;
  nodeIdToParentMap: Map<K1, K1>;
  linkDimensions: Map<K2, Dimensions>;
  promotedLinkDimensions: Map<string, Dimensions>;
  rootRef?: RefObject<HTMLDivElement>;
  nodeBoundsRef: RefObject<Map<K1, Bounds>>;
  promotedLinks: PromotedLinkId<K1, D2>[];
  normalizeLinks: boolean;
};

export function DiagramLayers<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>({
  nodes,
  links,
  promotedLinks,
  nodeRenderer,
  linkRenderer,
  states,
  nodeBounds,
  width,
  height,
  onNodeSizeChanged,
  onLinkSizeChanged,
  onPromotedLinkSizeChanged,
  labelDimensions,
  nodeLabelPositions,
  linkLabelPositions,
  supportsSelection,
  layoutOutput,
  zoom,
  nodeIdToParentMap,
  linkDimensions,
  promotedLinkDimensions,
  rootRef,
  combinedNodeBounds,
  nodeBoundsRef,
  normalizeLinks
}: DiagramLayersProps<K1, K2, D1, D2, L>) {
  const { backLinks, backPromotedLinks } = getLinksForNode<K1, K2, D2>(
    undefined,
    links,
    promotedLinks,
    nodeIdToParentMap
  );
  const { minX, minY } = getBounds(
    nodes,
    backLinks,
    backPromotedLinks,
    nodeBounds,
    linkDimensions,
    promotedLinkDimensions,
    zoom,
    rootRef
  );
  return (
    <>
      <DiagramNodeLayer
        nodes={nodes}
        nodeBounds={nodeBounds}
        states={states}
        nodeRenderer={nodeRenderer}
        onNodeSizeChanged={onNodeSizeChanged}
        onLinkSizeChanged={onLinkSizeChanged}
        onPromotedLinkSizeChanged={onPromotedLinkSizeChanged}
        supportsSelection={supportsSelection}
        nodeLabelPositions={nodeLabelPositions}
        linkLabelPositions={linkLabelPositions}
        labelDimensions={labelDimensions}
        linkDimensions={linkDimensions}
        promotedLinkDimensions={promotedLinkDimensions}
        rootRef={rootRef}
        links={links}
        promotedLinks={promotedLinks}
        nodeIdToParentMap={nodeIdToParentMap}
        linkRenderer={linkRenderer}
        layoutOutput={layoutOutput}
        width={width}
        height={height}
        zoom={zoom}
        minX={nodeBounds && minX !== Infinity ? minX : undefined}
        minY={nodeBounds && minY !== Infinity ? minY : undefined}
        combinedNodeBounds={combinedNodeBounds}
        nodeBoundsRef={nodeBoundsRef}
        normalizeLinks={normalizeLinks}
      />
    </>
  );
}
